import { apiService } from './api.service'
import {
  LoginRequest,
  LoginResponse,
  User,
  AuthTokens,
  AuthenticationResult,
  RefreshTokenRequest,
  Role,
} from '../types'
import { tokenUtils, mapAuthResult, toAuthTokens, hasPermission as matchPermission } from '../utils/auth.utils'
import { storage } from '../utils/storage.utils'

const USER_STORAGE_KEY = 'templatean_user'
const AUTH_PATH = '/v1/auth'

/** Shape of `GET /v1/users/me` (UserODataDto). */
interface CurrentUserDto {
  id: string
  tenantId: string
  email: string
  fullName: string
  roles?: Array<{ id: string; name: string; permissions?: string[] }>
}

const mapCurrentUser = (dto: CurrentUserDto, fallback?: User | null): User => {
  const roles: Role[] = (dto.roles ?? []).map(r => ({
    id: r.id,
    name: r.name,
    permissions: (r.permissions ?? []).map(name => ({
      id: name,
      name,
      resource: name.split('.')[0] ?? name,
      action: name.split('.')[1] ?? '',
    })),
  }))
  const rolePermissions = roles.flatMap(r => r.permissions.map(p => p.name))
  return {
    id: dto.id,
    email: dto.email,
    fullName: dto.fullName,
    tenantId: dto.tenantId,
    roles,
    // Login already gave us the flattened permission list; keep it if /me omits it.
    permissions: rolePermissions.length ? rolePermissions : fallback?.permissions ?? [],
  }
}

export class AuthService {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await apiService.post<AuthenticationResult>(`${AUTH_PATH}/login`, credentials)
    const result = mapAuthResult(response.data)

    tokenUtils.setTokens(result.tokens)
    await storage.set(USER_STORAGE_KEY, result.user)

    return result
  }

  async logout(): Promise<void> {
    try {
      if (tokenUtils.getTokens()) {
        await apiService.post(`${AUTH_PATH}/logout`)
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      tokenUtils.clearTokens()
      await storage.remove(USER_STORAGE_KEY)
    }
  }

  async refreshToken(refreshToken: string): Promise<AuthTokens> {
    const response = await apiService.post<AuthenticationResult>(`${AUTH_PATH}/refresh`, {
      refreshToken,
    } as RefreshTokenRequest)

    const tokens = toAuthTokens(response.data)
    tokenUtils.setTokens(tokens)
    return tokens
  }

  /** Synchronous read of the cached user (populated on login). */
  getCachedUser(): User | null {
    try {
      const raw = localStorage.getItem(USER_STORAGE_KEY)
      return raw ? (JSON.parse(raw) as User) : null
    } catch {
      return null
    }
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const cachedUser = await storage.get<User>(USER_STORAGE_KEY)
      if (cachedUser) return cachedUser

      const response = await apiService.get<CurrentUserDto>('/v1/users/me')
      const user = mapCurrentUser(response.data)
      await storage.set(USER_STORAGE_KEY, user)
      return user
    } catch (error) {
      return null
    }
  }

  async updateProfile(updates: Partial<User>): Promise<User> {
    const response = await apiService.put<CurrentUserDto>('/v1/users/me', updates)
    const user = mapCurrentUser(response.data, this.getCachedUser())
    await storage.set(USER_STORAGE_KEY, user)
    return user
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await apiService.post(`${AUTH_PATH}/change-password`, {
      currentPassword,
      newPassword,
    })
  }

  async forgotPassword(email: string): Promise<void> {
    await apiService.post(`${AUTH_PATH}/forgot-password`, { email })
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await apiService.post(`${AUTH_PATH}/reset-password`, {
      token,
      newPassword,
    })
  }

  async verifyEmail(token: string): Promise<void> {
    await apiService.post(`${AUTH_PATH}/verify-email`, { token })
  }

  async resendVerificationEmail(email: string): Promise<void> {
    await apiService.post(`${AUTH_PATH}/resend-verification`, { email })
  }

  isAuthenticated(): boolean {
    const tokens = tokenUtils.getTokens()
    return !!tokens && !tokenUtils.isTokenExpired(tokens.expiresIn)
  }

  hasPermission(permission: string): boolean {
    const user = this.getCachedUser()
    if (!user) return false
    if (matchPermission(user.permissions ?? [], permission)) return true
    return user.roles.some(role => role.permissions.some(p => p.name === permission))
  }

  hasRole(roleName: string): boolean {
    const user = this.getCachedUser()
    if (!user) return false
    return user.roles.some(role => role.name === roleName)
  }
}

export const authService = new AuthService()
